/**
 * API Helper Functions
 * React-Query와 함께 동작하는 일관된 API 호출 헬퍼
 */

import { apiConfig, getAllEndpoints } from './api.config';
import axiosInstance from './axios';
import { getI18nKey } from '@packages/types/errors';
import type {
  ApiErrorResponse,
  ApiSuccessListResponse,
  ApiSuccessResponse,
  ApiResponse,
  PaginationInfo,
} from '@packages/types/common/dto';
import type { AxiosResponse } from 'axios';

/**
 * API 엔드포인트 타입 정의 (동적 생성)
 */
type ApiEndpoint = ReturnType<typeof getAllEndpoints>[number];

interface ApiMeta {
  pagination?: PaginationInfo;
  message?: string;
  statusCode?: number;
  [key: string]: unknown;
}

interface ApiEnvelopeError {
  message: string;
  code?: string;
  details?: unknown;
  statusCode?: number;
}

interface ParsedApiResponse<ViewModel, Meta = ApiMeta> {
  data: ViewModel | null;
  meta: Meta | null;
  error: ApiEnvelopeError | null;
}

interface ParseApiResponseOptions<Raw, ViewModel> {
  mapData?: (raw: Raw) => ViewModel;
  defaultValue?: ViewModel;
}

type ApiPayload<Raw, Meta = ApiMeta> =
  | ApiSuccessResponse<Raw>
  | ApiSuccessListResponse<Raw extends unknown[] ? Raw : Raw[]>
  | ApiErrorResponse
  | ApiResponse<Raw>
  | {
      data?: Raw | null;
      meta?: Meta | null;
      error?: ApiEnvelopeError | null;
      message?: string;
      statusCode?: number;
      success?: boolean;
    };

const isAxiosResponse = <Raw, Meta = ApiMeta>(
  input: AxiosResponse<ApiPayload<Raw, Meta>> | ApiPayload<Raw, Meta>
): input is AxiosResponse<ApiPayload<Raw, Meta>> => 'data' in input && 'status' in input;

const pickPayload = <Raw, Meta = ApiMeta>(
  input: AxiosResponse<ApiPayload<Raw, Meta>> | ApiPayload<Raw, Meta>
): ApiPayload<Raw, Meta> => (isAxiosResponse<Raw, Meta>(input) ? input.data : input);

export const parseApiResponse = <Raw, ViewModel = Raw, Meta = ApiMeta>(
  input: AxiosResponse<ApiPayload<Raw, Meta>> | ApiPayload<Raw, Meta>,
  options: ParseApiResponseOptions<Raw, ViewModel> = {}
): ParsedApiResponse<ViewModel, Meta> => {
  const payload = pickPayload<Raw, Meta>(input) || {};
  const p = payload as Record<string, unknown>;

  const payloadData = p.data as Raw | null | undefined;
  const hasData = payloadData !== undefined && payloadData !== null;
  const mappedData = hasData
    ? options.mapData
      ? options.mapData(payloadData as Raw)
      : (payloadData as ViewModel)
    : (options.defaultValue ?? null);

  const payloadMeta = (p.meta as Meta) ?? null;
  const meta: Meta | null =
    payloadMeta ??
    ((('pagination' in p || 'message' in p || 'statusCode' in p) &&
      ({
        pagination: p.pagination,
        message: p.message,
        statusCode: p.statusCode,
      } as unknown as Meta)) ||
      null);

  const explicitError = (p.error as ApiEnvelopeError | null | undefined) ?? null;
  const inferredError =
    explicitError ??
    (payload && 'success' in payload && payload.success === false
      ? {
          message: (payload as ApiErrorResponse).message,
          statusCode: (payload as ApiErrorResponse).statusCode,
          details: (payload as ApiErrorResponse).details,
        }
      : null);

  return {
    data: (hasData ? mappedData : (options.defaultValue ?? null)) as ViewModel | null,
    meta,
    error: inferredError ?? null,
  };
};

export const extractApiData = <Raw, ViewModel = Raw, Meta = ApiMeta>(
  input: AxiosResponse<ApiPayload<Raw, Meta>> | ApiPayload<Raw, Meta>,
  options: ParseApiResponseOptions<Raw, ViewModel> = {}
): ViewModel => {
  const parsed = parseApiResponse<Raw, ViewModel, Meta>(input, options);

  if (parsed.error) {
    throw new Error(parsed.error.message);
  }

  return (parsed.data ?? (options.defaultValue as ViewModel)) as ViewModel;
};

/**
 * axios 에러(또는 임의의 에러)에서 표시용 i18n 키를 추출한다.
 *
 * 우선순위:
 *   1) 백엔드 표준 envelope의 `response.data.message`가 i18n 키(`scope:key`) 형식이면 그대로 사용
 *   2) I18nError / `xxx:yyy` 형식의 Error 메시지 (getI18nKey 위임)
 *   3) 위에 해당하지 않으면 fallbackKey
 *
 * 주의: `getI18nKey`는 "에러 객체"를 받도록 만들어졌으므로, 백엔드 message 문자열을
 * 그대로 넘기면 항상 기본 키가 반환된다. 이 헬퍼가 그 차이를 흡수한다.
 */
export const getApiErrorKey = (error: unknown, fallbackKey: string): string => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;

  if (typeof message === 'string' && message.includes(':')) {
    return message;
  }

  // class-validator 검증 오류는 message가 배열로 온다.
  // 배열을 보지 않으면 실제 사유가 가려지고 fallback만 표시된다.
  if (Array.isArray(message)) {
    const i18nKey = message.find(
      (item): item is string => typeof item === 'string' && item.includes(':')
    );
    if (i18nKey) {
      return i18nKey;
    }
  }

  return getI18nKey(error, fallbackKey);
};

/**
 * 엔드포인트가 유효한지 검증하는 함수
 */
const isValidEndpoint = (endpoint: string): endpoint is ApiEndpoint => {
  const [category, action] = endpoint.split('.');

  if (!category || !action) {
    return false;
  }

  const categoryEndpoints = apiConfig.endpoints[category];
  if (!categoryEndpoints) {
    return false;
  }

  return action in categoryEndpoints;
};

/**
 * 엔드포인트 경로를 가져오는 함수
 */
const getEndpointPath = (
  endpoint: ApiEndpoint,
  params?: Record<string, string | number>
): string => {
  // 런타임 검증
  if (!isValidEndpoint(endpoint)) {
    throw new Error(`Invalid endpoint: ${endpoint}`);
  }

  const [category, action] = endpoint.split('.') as [string, string];
  const categoryEndpoints = apiConfig.endpoints[category];
  const basePath = categoryEndpoints[action];

  if (!basePath) {
    throw new Error(`Unknown API action: ${action} in category: ${category}`);
  }

  // 파라미터가 있는 경우 URL에 적용
  if (params) {
    let path = basePath;
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, String(value));
    });
    return path;
  }

  return basePath;
};

/**
 * API 호출을 위한 헬퍼 함수들
 */
export const apiHelpers = {
  /**
   * GET 요청
   * endpoint는 'category.action' 형식으로 api.config.ts에 정의된 키를 사용
   */
  get: async <T = unknown>(
    endpoint: ApiEndpoint,
    params?: Record<string, string | number>,
    queryParams?: Record<string, string | number | boolean>
  ): Promise<AxiosResponse<ApiSuccessResponse<T>>> => {
    const url = getEndpointPath(endpoint, params);
    const response = await axiosInstance.get<ApiSuccessResponse<T>>(url, { params: queryParams });
    return response;
  },

  /**
   * POST 요청
   */
  post: async <T = unknown>(
    endpoint: ApiEndpoint,
    data?: unknown,
    params?: Record<string, string | number>,
    config?: { timeout?: number }
  ): Promise<AxiosResponse<ApiSuccessResponse<T>>> => {
    const url = getEndpointPath(endpoint, params);
    return axiosInstance.post<ApiSuccessResponse<T>>(url, data, config);
  },

  /**
   * PUT 요청
   */
  put: async <T = unknown>(
    endpoint: ApiEndpoint,
    data?: unknown,
    params?: Record<string, string | number>
  ): Promise<AxiosResponse<ApiSuccessResponse<T>>> => {
    const url = getEndpointPath(endpoint, params);
    return axiosInstance.put<ApiSuccessResponse<T>>(url, data);
  },

  /**
   * PATCH 요청
   */
  patch: async <T = unknown>(
    endpoint: ApiEndpoint,
    data?: unknown,
    params?: Record<string, string | number>,
    config?: { timeout?: number }
  ): Promise<AxiosResponse<ApiSuccessResponse<T>>> => {
    const url = getEndpointPath(endpoint, params);
    return axiosInstance.patch<ApiSuccessResponse<T>>(url, data, config);
  },

  /**
   * DELETE 요청
   */
  delete: async <T = unknown>(
    endpoint: ApiEndpoint,
    params?: Record<string, string | number>,
    config?: { data?: unknown; timeout?: number }
  ): Promise<AxiosResponse<ApiSuccessResponse<T>>> => {
    const url = getEndpointPath(endpoint, params);
    return axiosInstance.delete<ApiSuccessResponse<T>>(url, config);
  },
};
